import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MdShare, MdCheckCircle, MdDelete } from 'react-icons/md';

function ExecutionResults({ focusedExecution }) {
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({}); // { testIdx: boolean, 'scenario-idx-stepidx': boolean }
  const [error, setError] = useState('');

  useEffect(() => {
    fetchResults();
  }, []);

  useEffect(() => {
    if (!focusedExecution?.executionId) return;
    const targetResult = results.find((result) => result.id === focusedExecution.executionId);
    if (targetResult) {
      setSelectedResult(targetResult);
    }
  }, [focusedExecution, results]);

  useEffect(() => {
    if (selectedResult && focusedExecution?.autoDownload && focusedExecution?.executionId === selectedResult.id) {
      // Trigger download if autoDownload flag is set
      downloadDetailedReport(selectedResult);
    }
  }, [selectedResult, focusedExecution]);

  useEffect(() => {
    if (!selectedResult) {
      setExpandedSteps({});
      return;
    }

    // Auto-expand failed steps
    const newExpanded = {};
    (selectedResult.tests || []).forEach((test, testIdx) => {
      if (test.payloadType === 'suite_scenario') {
        const scenarioPassed = test.passed;
        if (!scenarioPassed) {
          newExpanded[testIdx] = true;
          // Also expand specific failed steps inside scenario
          (test.results || []).forEach((res, resIdx) => {
            if (!res.passed) {
              newExpanded[`${testIdx}-${resIdx}`] = true;
            }
          });
        }
      } else {
        const primary = getPrimaryResult(test);
        if (!primary.passed) {
          newExpanded[testIdx] = true;
        }
      }
    });
    setExpandedSteps(newExpanded);
  }, [selectedResult]);

  const toggleStep = (key) => {
    setExpandedSteps(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };



  const fetchResults = async () => {
    try {
      const response = await axios.get('/api/execution');
      const list = Array.isArray(response.data) ? [...response.data].reverse() : [];
      setResults(list);
      setError(''); // Clear any previous error
    } catch (fetchError) {
      // If nothing has run yet (404), don't show an error
      if (fetchError.response?.status === 404) {
        setResults([]);
        return;
      }
      setError('Failed to fetch results: ' + (fetchError.response?.data?.error || fetchError.message));
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all execution results? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete('/api/execution');
      setResults([]);
      setSelectedResult(null);
    } catch (err) {
      setError('Failed to clear history: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteResult = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this execution result?')) {
      return;
    }

    try {
      await axios.delete(`/api/execution/${id}`);
      setResults(prev => prev.filter(r => r.id !== id));
      if (selectedResult?.id === id) {
        setSelectedResult(null);
      }
    } catch (err) {
      setError('Failed to delete result: ' + (err.response?.data?.error || err.message));
    }
  };

  const getPassPercentage = (summary) => {
    const total = Number(summary?.total || 0);
    const passed = Number(summary?.passed || 0);
    if (!total) return 0;
    return Math.round((passed / total) * 100);
  };

  const getPrimaryResult = (test) => {
    if (!test || !Array.isArray(test.results) || test.results.length === 0) {
      return { passed: test?.passed ?? false };
    }
    const r = test.results[0] || {};
    return { ...r, passed: test.passed ?? r.passed };
  };

  const escapeHtml = (value = '') => {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const tryParseJsonString = (value) => {
    try {
      return {
        ok: true,
        value: JSON.parse(value)
      };
    } catch (error) {
      return { ok: false };
    }
  };

  const normalizeCsvEscapedJsonString = (value = '') => {
    let normalized = String(value || '').trim();
    if (!normalized) return normalized;

    if (normalized.startsWith('"') && normalized.endsWith('"')) {
      normalized = normalized.slice(1, -1);
    }

    normalized = normalized.replace(/\\"/g, '"');
    normalized = normalized.replace(/""/g, '"');
    return normalized.trim();
  };

  function parseStructuredString(value, depth = 0) {
    if (depth > 4) return value;
    const trimmed = String(value || '').trim();
    if (!trimmed) return value;

    const directParsed = tryParseJsonString(trimmed);
    if (directParsed.ok) {
      if (typeof directParsed.value === 'string') {
        return parseStructuredString(directParsed.value, depth + 1);
      }
      return normalizeStructuredValue(directParsed.value, depth + 1);
    }

    const normalizedEscapedJson = normalizeCsvEscapedJsonString(trimmed);
    if (normalizedEscapedJson && normalizedEscapedJson !== trimmed) {
      const normalizedParsed = tryParseJsonString(normalizedEscapedJson);
      if (normalizedParsed.ok) {
        if (typeof normalizedParsed.value === 'string') {
          return parseStructuredString(normalizedParsed.value, depth + 1);
        }
        return normalizeStructuredValue(normalizedParsed.value, depth + 1);
      }
    }

    return value;
  }

  function normalizeStructuredValue(value, depth = 0) {
    if (depth > 6) return value;

    if (typeof value === 'string') {
      return parseStructuredString(value, depth + 1);
    }

    if (Array.isArray(value)) {
      return value.map((item) => normalizeStructuredValue(item, depth + 1));
    }

    if (value && typeof value === 'object') {
      const next = {};
      Object.keys(value).forEach((key) => {
        next[key] = normalizeStructuredValue(value[key], depth + 1);
      });
      return next;
    }

    return value;
  }

  const getPayloadSource = (test) => {
    if (!test) return null;
    if (test.csvRow !== undefined) return test.csvRow;
    if (test.payload !== undefined) return test.payload;
    return null;
  };

  const getResponseSource = (result) => {
    const source = result?.actualResponse ?? result?.responsePreview ?? result?.data ?? result?.output;
    if (source === '') return '(empty response body)';
    return source ?? '-';
  };

  const formatJsonText = (value) => {
    if (value === undefined || value === null) return '-';

    const normalizedValue = normalizeStructuredValue(value);
    if (typeof normalizedValue === 'string') {
      return normalizedValue.trim() ? normalizedValue : '(empty)';
    }

    try {
      return JSON.stringify(normalizedValue, null, 2);
    } catch (stringifyError) {
      return String(normalizedValue);
    }
  };

  const polarToCartesian = (cx, cy, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: cx + radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians)
    };
  };

  const describePieArc = (cx, cy, radius, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return [
      `M ${cx} ${cy}`,
      `L ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
      'Z'
    ].join(' ');
  };

  const createPieChartSvg = (passed, failed) => {
    const total = passed + failed;
    if (total <= 0) {
      return `
        <svg viewBox="0 0 240 240" width="220" height="220" aria-label="No tests">
          <circle cx="120" cy="120" r="92" fill="#e5e7eb"></circle>
          <circle cx="120" cy="120" r="58" fill="#ffffff"></circle>
          <text x="120" y="126" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" fill="#374151">No Data</text>
        </svg>
      `;
    }

    const passRate = Math.round((passed / total) * 100);
    const slices = [];
    let currentAngle = 0;

    const addSlice = (count, color) => {
      if (count <= 0) return;
      const sweep = (count / total) * 360;
      const endAngle = currentAngle + sweep;

      if (sweep >= 359.999) {
        slices.push(`<circle cx="120" cy="120" r="92" fill="${color}"></circle>`);
      } else {
        slices.push(`<path d="${describePieArc(120, 120, 92, currentAngle, endAngle)}" fill="${color}"></path>`);
      }

      currentAngle = endAngle;
    };

    addSlice(passed, '#22c55e');
    addSlice(failed, '#ef4444');

    return `
      <svg viewBox="0 0 240 240" width="220" height="220" aria-label="Pass fail distribution">
        ${slices.join('')}
        <circle cx="120" cy="120" r="58" fill="#ffffff"></circle>
        <text x="120" y="118" text-anchor="middle" font-family="Arial, sans-serif" font-size="28" font-weight="700" fill="#111827">${passRate}%</text>
        <text x="120" y="140" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" fill="#6b7280">Pass Rate</text>
      </svg>
    `;
  };

  const buildDetailedReportHtml = (result) => {
    const summary = result?.summary || { total: 0, passed: 0, failed: 0 };
    const tests = Array.isArray(result?.tests) ? result.tests : [];
    const reportGeneratedOn = new Date().toLocaleString();
    const startedOn = result?.startTime ? new Date(result.startTime).toLocaleString() : '-';
    const duration = result?.duration ?? '-';
    const passRate = getPassPercentage(summary);
    const pieChartSvg = createPieChartSvg(Number(summary.passed || 0), Number(summary.failed || 0));

    const tableRows = tests
      .map((test, index) => {
        if (test.payloadType === 'suite_scenario') {
          return `
            <tr>
              <td>${index + 1}</td>
              <td colspan="4"><strong>${escapeHtml(test.testStepName)}</strong> (Scenario)</td>
              <td>-</td>
              <td>-</td>
              <td class="${test.passed ? 'ok' : 'bad'}">${test.passed ? 'Passed' : 'Failed'}</td>
              <td colspan="3">Scenario breakdown below</td>
            </tr>
          `;
        }
        const r = getPrimaryResult(test);
        const resultLabel = r.passed ? 'Passed' : 'Failed';
        const payloadText = formatJsonText(getPayloadSource(test));
        const failureText = r.failureReason || '-';
        const errorText = r.error || '-';
        const responseTime = Number.isFinite(r.responseTime) ? `${r.responseTime} ms` : '-';

        return `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(test.testStepName || test.apiName || '-')}</td>
            <td>
              ${escapeHtml(test.apiName || '-')}
              ${r.apiMethod ? `<br/><small style="color: #64748b;">${escapeHtml(r.apiMethod)} ${escapeHtml(r.fullUrl || '')}</small>` : ''}
            </td>
            <td>${escapeHtml(test.payloadType || '-')}</td>
            <td>${escapeHtml(r.expectedStatus ?? '-')}</td>
            <td>${escapeHtml(r.status ?? '-')}</td>
            <td>${escapeHtml(responseTime)}</td>
            <td class="${r.passed ? 'ok' : 'bad'}">${resultLabel}</td>
            <td>${escapeHtml(failureText)}</td>
            <td>${escapeHtml(errorText)}</td>
            <td><pre>${escapeHtml(payloadText)}</pre></td>
          </tr>
        `;
      })
      .join('');

    const detailBlocks = tests
      .map((test, index) => {
        if (test.payloadType === 'suite_scenario') {
          return `
            <div class="test-block" style="margin-bottom: 30px; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; background: #fff;">
              <h3 style="margin-top: 0;">#${index + 1} ${escapeHtml(test.testStepName)} (Scenario)</h3>
              <p><strong>Result:</strong> <span class="${test.passed ? 'ok' : 'bad'}">${test.passed ? 'Passed' : 'Failed'}</span></p>
              <div class="results-list" style="margin-top: 15px;">
                ${(test.results || []).map((res, ridx) => {
                  const rText = formatJsonText(res.actualResponse ?? res.responsePreview ?? res.output ?? res.error);
                  const pText = formatJsonText(res.payload);
                  return `
                    <div class="result-item" style="margin-bottom: 20px; padding: 15px; border: 1px solid #f1f5f9; background: #fdfdfd; border-radius: 8px;">
                      <div style="font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #f1f5f9; padding-bottom: 5px;">
                        Step ${ridx + 1}: ${escapeHtml(res.stepName)} - <span class="${res.passed ? 'ok' : 'bad'}">${res.passed ? 'PASS' : 'FAIL'}</span> ${res.status ? `(${res.status})` : ''}
                        ${res.apiMethod ? `<br/><small style="color: #64748b; font-weight: normal;">${escapeHtml(res.apiMethod)} ${escapeHtml(res.fullUrl || res.apiUrl || '')}</small>` : ''}
                      </div>
                      <div class="dual">
                        <div>
                          <h4>Payload</h4>
                          <pre>${escapeHtml(pText)}</pre>
                        </div>
                        <div>
                          <h4>Response / Output</h4>
                          <pre>${escapeHtml(rText)}</pre>
                        </div>
                      </div>
                      ${res.failureReason ? `<p style="color: #ef4444; font-size: 11px; margin-top: 5px;"><strong>Failure:</strong> ${escapeHtml(res.failureReason)}</p>` : ''}
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }

        const r = getPrimaryResult(test);
        const payloadText = formatJsonText(getPayloadSource(test));
        const responseText = formatJsonText(getResponseSource(test));

        return `
          <section class="detail-card">
            <h3>#${index + 1} ${escapeHtml(test.testStepName || test.apiName || 'Test')}</h3>
            <p><strong>API:</strong> ${escapeHtml(test.apiName || '-')} ${r.apiMethod ? `(${escapeHtml(r.apiMethod)} ${escapeHtml(r.fullUrl || '')})` : ''}</p>
            <p><strong>Payload Type:</strong> ${escapeHtml(test.payloadType || '-')}</p>
            <p><strong>Status:</strong> ${escapeHtml(r.status ?? '-')} (${r.passed ? 'Passed' : 'Failed'})</p>
            <p><strong>Expected:</strong> ${escapeHtml(r.expectedStatus ?? '-')} | <strong>Response Time:</strong> ${escapeHtml(Number.isFinite(r.responseTime) ? `${r.responseTime} ms` : '-')}</p>
            ${r.failureReason ? `<p><strong>Failure Reason:</strong> ${escapeHtml(r.failureReason)}</p>` : ''}
            ${r.error ? `<p><strong>Error:</strong> ${escapeHtml(r.error)}</p>` : ''}
            <div class="dual">
              <div>
                <h4>Payload</h4>
                <pre>${escapeHtml(payloadText)}</pre>
              </div>
              <div>
                <h4>Response</h4>
                <pre>${escapeHtml(responseText)}</pre>
              </div>
            </div>
          </section>
        `;
      })
      .join('');

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Execution Report - ${escapeHtml(result?.suiteName || 'Execution')}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 24px; font-family: Arial, sans-serif; color: #111827; background: #f8fafc; }
    .wrapper { max-width: 1200px; margin: 0 auto; }
    .toolbar { display: flex; justify-content: flex-end; margin-bottom: 12px; }
    .close-btn { border: 1px solid #cbd5e1; background: #f8fafc; color: #0f172a; border-radius: 8px; padding: 8px 14px; font-weight: 700; cursor: pointer; }
    .close-btn:hover { background: #e2e8f0; border-color: #94a3b8; }
    .header { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
    .header h1 { margin: 0 0 8px; font-size: 24px; }
    .meta { color: #6b7280; font-size: 13px; }
    .summary-grid { display: grid; grid-template-columns: 240px 1fr; gap: 20px; align-items: center; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
    .stats { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 12px; }
    .stat { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .stat .k { color: #6b7280; font-size: 12px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
    .stat .v { font-size: 20px; font-weight: 700; }
    .legend { display: flex; gap: 14px; margin-top: 10px; font-size: 13px; color: #374151; }
    .dot { display: inline-block; width: 10px; height: 10px; border-radius: 999px; margin-right: 6px; }
    .dot.pass { background: #22c55e; }
    .dot.fail { background: #ef4444; }
    .section { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
    .section h2 { margin: 0 0 14px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { border: 1px solid #e5e7eb; padding: 8px; vertical-align: top; text-align: left; }
    th { background: #f1f5f9; }
    td pre { margin: 0; white-space: pre-wrap; max-width: 260px; overflow-wrap: anywhere; font-size: 11px; }
    .ok { color: #166534; font-weight: 700; }
    .bad { color: #991b1b; font-weight: 700; }
    .detail-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; margin-bottom: 14px; background: #ffffff; }
    .detail-card h3 { margin: 0 0 8px; }
    .detail-card p { margin: 4px 0; font-size: 13px; }
    .dual { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 10px; }
    .dual h4 { margin: 0 0 6px; font-size: 13px; }
    .dual pre { margin: 0; padding: 10px; border: 1px solid #e5e7eb; background: #f8fafc; border-radius: 8px; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 11px; min-height: 80px; }
    .footer-note { color: #6b7280; font-size: 12px; text-align: right; margin-top: 10px; }
  </style>
</head>
<body>
  <div class="wrapper">
    <div class="toolbar">
      <button type="button" class="close-btn" onclick="window.close()">Close</button>
    </div>
    <div class="header">
      <h1>${escapeHtml(result?.suiteName || 'Execution Result')}</h1>
      <div class="meta">Execution ID: ${escapeHtml(result?.id || '-')} | Started: ${escapeHtml(startedOn)} | Duration: ${escapeHtml(`${duration} ms`)}</div>
    </div>

    <div class="summary-grid">
      <div>
        ${pieChartSvg}
        <div class="legend">
          <span><span class="dot pass"></span>Passed: ${summary.passed || 0}</span>
          <span><span class="dot fail"></span>Failed: ${summary.failed || 0}</span>
        </div>
      </div>
      <div class="stats">
        <div class="stat"><div class="k">Total</div><div class="v">${summary.total || 0}</div></div>
        <div class="stat"><div class="k">Passed</div><div class="v">${summary.passed || 0}</div></div>
        <div class="stat"><div class="k">Failed</div><div class="v">${summary.failed || 0}</div></div>
        <div class="stat"><div class="k">Pass Rate</div><div class="v">${passRate}%</div></div>
      </div>
    </div>

    <div class="section">
      <h2>Execution Table</h2>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Test Step</th>
            <th>API</th>
            <th>Payload Type</th>
            <th>Expected</th>
            <th>Actual</th>
            <th>Response Time</th>
            <th>Result</th>
            <th>Failure Reason</th>
            <th>Error</th>
            <th>Payload</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </div>

    <div class="section">
      <h2>Detailed Test Breakdown</h2>
      ${detailBlocks}
    </div>

    <div class="footer-note">Generated on ${escapeHtml(reportGeneratedOn)}</div>
  </div>
</body>
</html>
    `;
  };

  const buildSummaryReportHtml = (result) => {
    const summary = result?.summary || { total: 0, passed: 0, failed: 0 };
    const tests = Array.isArray(result?.tests) ? result.tests : [];
    const reportGeneratedOn = new Date().toLocaleString();
    const startedOn = result?.startTime ? new Date(result.startTime).toLocaleString() : '-';
    const duration = result?.duration ?? '-';
    const passRate = getPassPercentage(summary);
    const pieChartSvg = createPieChartSvg(Number(summary.passed || 0), Number(summary.failed || 0));

    const summaryRows = tests
      .map((test, index) => {
        if (test.payloadType === 'suite_scenario') {
          return `
            <tr>
              <td>${index + 1}</td>
              <td colspan="2"><strong>${escapeHtml(test.testStepName)}</strong> (Scenario)</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td class="${test.passed ? 'ok' : 'bad'}">${test.passed ? 'Passed' : 'Failed'}</td>
            </tr>
          `;
        }
        const r = getPrimaryResult(test);
        const responseTime = Number.isFinite(r.responseTime) ? `${r.responseTime} ms` : '-';
        return `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(test.testStepName || test.apiName || '-')}</td>
            <td>
              ${escapeHtml(test.apiName || '-')}
              ${r.apiMethod ? `<br/><small style="color: #64748b;">${escapeHtml(r.apiMethod)} ${escapeHtml(r.fullUrl || '')}</small>` : ''}
            </td>
            <td>${escapeHtml(test.payloadType || '-')}</td>
            <td>${escapeHtml(r.expectedStatus ?? '-')}</td>
            <td>${escapeHtml(r.status ?? '-')}</td>
            <td>${escapeHtml(responseTime)}</td>
            <td class="${r.passed ? 'ok' : 'bad'}">${r.passed ? 'Passed' : 'Failed'}</td>
          </tr>
        `;
      })
      .join('');

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Summary Report - ${escapeHtml(result?.suiteName || 'Execution')}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 24px; font-family: Arial, sans-serif; color: #111827; background: #f8fafc; }
    .wrapper { max-width: 1100px; margin: 0 auto; }
    .toolbar { display: flex; justify-content: flex-end; margin-bottom: 12px; }
    .close-btn { border: 1px solid #cbd5e1; background: #f8fafc; color: #0f172a; border-radius: 8px; padding: 8px 14px; font-weight: 700; cursor: pointer; }
    .close-btn:hover { background: #e2e8f0; border-color: #94a3b8; }
    .header { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
    .header h1 { margin: 0 0 8px; font-size: 24px; }
    .meta { color: #6b7280; font-size: 13px; }
    .summary-grid { display: grid; grid-template-columns: 240px 1fr; gap: 20px; align-items: center; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
    .stats { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 12px; }
    .stat { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    .stat .k { color: #6b7280; font-size: 12px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
    .stat .v { font-size: 20px; font-weight: 700; }
    .legend { display: flex; gap: 14px; margin-top: 10px; font-size: 13px; color: #374151; }
    .dot { display: inline-block; width: 10px; height: 10px; border-radius: 999px; margin-right: 6px; }
    .dot.pass { background: #22c55e; }
    .dot.fail { background: #ef4444; }
    .section { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
    .section h2 { margin: 0 0 14px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
    th { background: #f1f5f9; }
    .ok { color: #166534; font-weight: 700; }
    .bad { color: #991b1b; font-weight: 700; }
    .footer-note { color: #6b7280; font-size: 12px; text-align: right; margin-top: 10px; }
  </style>
</head>
<body>
  <div class="wrapper">
    <div class="toolbar">
      <button type="button" class="close-btn" onclick="window.close()">Close</button>
    </div>
    <div class="header">
      <h1>${escapeHtml(result?.suiteName || 'Execution Summary')}</h1>
      <div class="meta">Execution ID: ${escapeHtml(result?.id || '-')} | Started: ${escapeHtml(startedOn)} | Duration: ${escapeHtml(`${duration} ms`)}</div>
    </div>

    <div class="summary-grid">
      <div>
        ${pieChartSvg}
        <div class="legend">
          <span><span class="dot pass"></span>Passed: ${summary.passed || 0}</span>
          <span><span class="dot fail"></span>Failed: ${summary.failed || 0}</span>
        </div>
      </div>
      <div class="stats">
        <div class="stat"><div class="k">Total</div><div class="v">${summary.total || 0}</div></div>
        <div class="stat"><div class="k">Passed</div><div class="v">${summary.passed || 0}</div></div>
        <div class="stat"><div class="k">Failed</div><div class="v">${summary.failed || 0}</div></div>
        <div class="stat"><div class="k">Pass Rate</div><div class="v">${passRate}%</div></div>
      </div>
    </div>

    <div class="section">
      <h2>Test Summary</h2>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Test Step</th>
            <th>API</th>
            <th>Payload Type</th>
            <th>Expected</th>
            <th>Actual</th>
            <th>Response Time</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          ${summaryRows}
        </tbody>
      </table>
    </div>

    <div class="footer-note">Generated on ${escapeHtml(reportGeneratedOn)}</div>
  </div>
</body>
</html>
    `;
  };

  const getReportFileName = (result, type) => {
    const safeSuiteName = String(result?.suiteName || 'execution_report').replace(/[^a-z0-9_-]+/gi, '_');
    const timestamp = new Date(result?.startTime || Date.now()).toISOString().replace(/[:.]/g, '-');
    return `${safeSuiteName}_${type}_${timestamp}.html`;
  };

  const downloadReportHtml = (html, fileName) => {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  };

  const viewReportHtml = (html) => {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const downloadDetailedReport = (result) => {
    if (!result) return;
    const html = buildDetailedReportHtml(result);
    downloadReportHtml(html, getReportFileName(result, 'detailed_report'));
  };

  const viewDetailedReport = (result) => {
    if (!result) return;
    const html = buildDetailedReportHtml(result);
    viewReportHtml(html);
  };

  const downloadSummaryReport = (result) => {
    if (!result) return;
    const html = buildSummaryReportHtml(result);
    downloadReportHtml(html, getReportFileName(result, 'summary_report'));
  };

  const viewSummaryReport = (result) => {
    if (!result) return;
    const html = buildSummaryReportHtml(result);
    viewReportHtml(html);
  };

  return (
    <div>
      <h1>Execution Results</h1>

      {error && <div className="error">{error}</div>}

      <div className="results-layout">
        <div className="results-column">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 style={{ margin: 0 }}>Test Executions</h2>
            {results.length > 0 && (
              <button 
                className="btn btn-danger btn-sm" 
                style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={handleClearHistory}
              >
                <MdDelete /> Clear All
              </button>
            )}
          </div>

          {results.length === 0 ? (
            <div className="card">
              <p>No test executions yet. Execute a test suite or test step to see results.</p>
            </div>
          ) : (
            <div>
              {results.map(result => (
                <div
                  key={result.id}
                  className="card"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedResult?.id === result.id ? 'rgba(0, 144, 231, 0.12)' : 'var(--bg-panel)',
                    borderLeft: `4px solid ${Number(result.summary?.failed || 0) === 0 ? '#51cf66' : '#ff6b6b'}`
                  }}
                  onClick={() => setSelectedResult(result)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <h3 style={{ margin: 0 }}>{result.suiteName}</h3>
                        <button 
                          className="icon-btn" 
                          style={{ width: '24px', height: '24px', fontSize: '14px', border: 'none', background: 'transparent' }}
                          onClick={(e) => handleDeleteResult(result.id, e)}
                          title="Delete Result"
                        >
                          <MdDelete />
                        </button>
                      </div>
                      <small>{result.startTime ? new Date(result.startTime).toLocaleString() : '-'}</small>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                    <div style={{ textAlign: 'left' }}>
                      <span className={`badge ${Number(result.summary?.failed || 0) === 0 ? 'badge-success' : 'badge-danger'}`}>
                        {Number(result.summary?.passed || 0)}/{Number(result.summary?.total || 0)} Passed ({getPassPercentage(result.summary)}%)
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
                        {result.duration ?? '-'}ms
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="results-column">
          <h2>Result Details</h2>
          {selectedResult ? (
            <div className="card">
              <div className="result-header">
                <h3 style={{ margin: 0 }}>{selectedResult.suiteName}</h3>
                <div className="report-actions-grid">
                  <div className="report-action-card">
                    <p>Summary Report</p>
                    <div className="report-action-buttons">
                      <button className="btn btn-report-view" onClick={() => viewSummaryReport(selectedResult)}>
                        View
                      </button>
                      <button className="btn btn-report-download" onClick={() => downloadSummaryReport(selectedResult)}>
                        Download
                      </button>
                    </div>
                  </div>
                  <div className="report-action-card">
                    <p>Detailed Report</p>
                    <div className="report-action-buttons">
                      <button className="btn btn-report-view" onClick={() => viewDetailedReport(selectedResult)}>
                        View
                      </button>
                      <button className="btn btn-report-download" onClick={() => downloadDetailedReport(selectedResult)}>
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                <p style={{ margin: 0 }}><strong>Execution ID:</strong> {selectedResult.id}</p>
              </div>
              <p><strong>Started:</strong> {selectedResult.startTime ? new Date(selectedResult.startTime).toLocaleString() : '-'}</p>
              <p><strong>Duration:</strong> {selectedResult.duration ?? '-'}ms</p>

              <div style={{ marginTop: '20px' }}>
                <h4>Summary</h4>
                <p><span className="badge badge-success">Passed: {Number(selectedResult.summary?.passed || 0)}</span></p>
                <p><span className="badge badge-danger">Failed: {Number(selectedResult.summary?.failed || 0)}</span></p>
                <p><strong>Total:</strong> {Number(selectedResult.summary?.total || 0)}</p>
                {selectedResult.capturedData && Object.keys(selectedResult.capturedData).length > 0 && (
                  <details style={{ marginTop: '10px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--primary)', fontWeight: 'bold' }}>
                      View Captured Data
                    </summary>
                    <pre
                      style={{
                        margin: '10px 0',
                        padding: '10px',
                        backgroundColor: 'var(--bg-input)',
                        border: '1px solid var(--border)',
                        borderRadius: '5px',
                        fontSize: '11px',
                        color: 'var(--text-main)',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '220px',
                        overflow: 'auto'
                      }}
                    >
                      {formatJsonText(selectedResult.capturedData)}
                    </pre>
                  </details>
                )}
              </div>

              <div style={{ marginTop: '20px' }}>
                <h4>Test Details</h4>
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {(selectedResult.tests || []).map((test, index) => {
                    if (test.payloadType === 'suite_scenario') {
                      return (
                        <div key={index} style={{ padding: '15px', borderBottom: '1px solid var(--border)', backgroundColor: test.passed ? 'rgba(0, 210, 91, 0.05)' : 'rgba(252, 66, 74, 0.05)' }}>
                          <div 
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: expandedSteps[index] ? '10px' : '0', cursor: 'pointer' }}
                            onClick={() => toggleStep(index)}
                          >
                            <h4 style={{ margin: 0 }}>
                              {expandedSteps[index] ? '▾ ' : '▸ '}
                              {test.testStepName}
                            </h4>
                            <span className={`badge ${test.passed ? 'badge-success' : 'badge-danger'}`}>
                              {test.passed ? 'Passed' : 'Failed'}
                            </span>
                          </div>
                          {expandedSteps[index] && (
                            <div style={{ display: 'grid', gap: '10px' }}>
                              {(test.results || []).map((res, ridx) => {
                                const responseText = formatJsonText(res.actualResponse ?? res.responsePreview ?? res.output ?? res.error);
                                const payloadText = formatJsonText(res.payload);
                                const capturedText = formatJsonText(res.capturedValues || null);
                                return (
                                  <div key={ridx} style={{ padding: '10px', backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                                    <div 
                                      style={{ marginBottom: expandedSteps[`${index}-${ridx}`] ? '8px' : '0', cursor: 'pointer' }}
                                      onClick={(e) => { e.stopPropagation(); toggleStep(`${index}-${ridx}`); }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 'bold' }}>
                                        <span>
                                          {expandedSteps[`${index}-${ridx}`] ? '▾ ' : '▸ '}
                                          Step {ridx + 1}: {res.stepName}
                                        </span>
                                        <span style={{ color: res.passed ? 'var(--success)' : 'var(--danger)' }}>
                                          {res.passed ? 'PASS' : 'FAIL'} {res.status ? `(${res.status})` : ''}
                                        </span>
                                      </div>
                                      {res.apiName && (
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                          <strong>API:</strong> {res.apiName}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {expandedSteps[`${index}-${ridx}`] && (
                                      <>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                                          {(res.apiMethod || res.fullUrl || res.apiUrl) && (
                                            <> ({res.apiMethod && `${res.apiMethod} `}{res.fullUrl || res.apiUrl})</>
                                          )}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '11px' }}>
                                          <div>
                                            {res.payloadType === 'custom_function' ? (
                                              <>
                                                <span style={{ color: 'var(--text-muted)' }}>Stored As:</span>
                                                <pre style={{ margin: '4px 0', padding: '6px', backgroundColor: 'var(--bg-input)', borderRadius: '4px', maxHeight: '80px', overflow: 'auto' }}>
                                                  {res.storeAs || (res.capturedValues ? Object.keys(res.capturedValues).join(', ') : '-')}
                                                </pre>
                                              </>
                                            ) : (
                                              <>
                                                <span style={{ color: 'var(--text-muted)' }}>Payload:</span>
                                                <pre style={{ margin: '4px 0', padding: '6px', backgroundColor: 'var(--bg-input)', borderRadius: '4px', maxHeight: '80px', overflow: 'auto' }}>{payloadText}</pre>
                                              </>
                                            )}
                                          </div>
                                          <div>
                                            <span style={{ color: 'var(--text-muted)' }}>Response:</span>
                                            <pre style={{ margin: '4px 0', padding: '6px', backgroundColor: 'var(--bg-input)', borderRadius: '4px', maxHeight: '80px', overflow: 'auto' }}>{responseText}</pre>
                                          </div>
                                        </div>

                                        {res.capturedValues && Object.keys(res.capturedValues).length > 0 && (
                                          <div style={{ marginTop: '5px', fontSize: '11px', color: 'var(--success)' }}>
                                            <strong>Captured:</strong> {capturedText}
                                          </div>
                                        )}
                                        {res.failureReason && (
                                          <div style={{ marginTop: '5px', fontSize: '11px', color: 'var(--danger)', fontWeight: 'bold' }}>
                                            Failure: {res.failureReason}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    const testResult = getPrimaryResult(test);
                    const responseText = formatJsonText(getResponseSource(testResult));
                    const payloadText = formatJsonText(getPayloadSource(test));
                    const capturedText = formatJsonText(testResult.capturedValues || null);

                    return (
                      <div
                        key={index}
                        style={{
                          padding: '10px',
                          borderBottom: '1px solid var(--border)',
                          backgroundColor: testResult.passed ? 'rgba(0, 210, 91, 0.09)' : 'rgba(252, 66, 74, 0.09)'
                        }}
                      >
                        <div 
                          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          onClick={() => toggleStep(index)}
                        >
                          <div>
                            <strong>
                              {expandedSteps[index] ? '▾ ' : '▸ '}
                              {test.testStepName || test.apiName}
                            </strong>
                            {test.testStepName && <span style={{ marginLeft: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>({test.apiName})</span>}
                          </div>
                          <span
                             className="badge"
                             style={{
                               backgroundColor: testResult.passed ? 'rgba(0, 210, 91, 0.2)' : 'rgba(252, 66, 74, 0.2)',
                               color: testResult.passed ? 'var(--success)' : 'var(--danger)'
                             }}
                           >
                            {testResult.status ?? '-'} {testResult.passed ? 'Passed' : 'Failed'}
                          </span>
                        </div>

                        {expandedSteps[index] && (
                          <div style={{ marginTop: '10px' }}>
                            {(testResult.apiMethod || testResult.fullUrl) && (
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                <strong>Full API:</strong> {testResult.apiMethod || ''} {testResult.fullUrl || ''}
                              </div>
                            )}
                            <div style={{ margin: '8px 0' }}>
                              <strong style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                {test.payloadType === 'specific' ? 'Payload:' : 'CSV Data:'}
                              </strong>
                              <pre
                                style={{
                                  margin: '6px 0 0 0',
                                  padding: '8px',
                                  backgroundColor: 'var(--bg-input)',
                                  border: '1px solid var(--border)',
                                  borderRadius: '5px',
                                  fontSize: '11px',
                                  color: 'var(--text-main)',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: '180px',
                                  overflow: 'auto'
                                }}
                              >
                                {payloadText}
                              </pre>
                            </div>
                            <p style={{ margin: '5px 0', fontSize: '12px' }}>
                              <span className={`badge ${test.payloadType === 'specific' ? 'badge-success' : 'badge-warning'}`}>
                                {test.payloadType === 'specific' ? 'Specific Payload' : 'Data File'}
                              </span>
                            </p>
                            <small>
                              Expected: {testResult.expectedStatus ?? '-'} | Response Time: {Number.isFinite(testResult.responseTime) ? `${testResult.responseTime}ms` : '-'}
                            </small>
                            {testResult.failureReason && (
                              <p style={{ margin: '5px 0', fontSize: '12px', color: 'var(--danger)', fontWeight: 'bold' }}>
                                Failure: {testResult.failureReason}
                              </p>
                            )}
                            {testResult.error && (
                              <p style={{ margin: '5px 0', fontSize: '12px', color: 'var(--danger)' }}>
                                Error: {testResult.error}
                              </p>
                            )}
                            {testResult.capturedValues && Object.keys(testResult.capturedValues).length > 0 && (
                              <p style={{ margin: '5px 0', fontSize: '12px', color: 'var(--success)' }}>
                                <strong>Captured:</strong> {capturedText}
                              </p>
                            )}
                            <details style={{ margin: '10px 0' }} open>
                              <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--primary)', fontWeight: 'bold' }}>
                                View Response
                              </summary>
                              <pre
                                style={{
                                  margin: '10px 0',
                                  padding: '10px',
                                  backgroundColor: 'var(--bg-input)',
                                  border: '1px solid var(--border)',
                                  borderRadius: '5px',
                                  fontSize: '11px',
                                  color: 'var(--text-main)',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: '300px',
                                  overflow: 'auto'
                                }}
                              >
                                {responseText}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <p>Select an execution to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExecutionResults;
